import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { useClients } from "@/contexts/ClientContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate, useLocation } from "react-router-dom";
import { Download, FileText, Sparkles, Loader2, Save, Clock, Eye, Trash2, FileCheck, Building2, Layers, Globe2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { formatAuDateTime } from "@/lib/dateAu";

// Special sentinel for the "all properties" scope. Anything other than this
// string is treated as a real business_unit_id. We use a literal so a
// future addition of a property literally called "overarching" still works.
const SCOPE_OVERARCHING = "__overarching__";

export default function Reports() {
  const { selected } = useClients();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const portalPrefix = location.pathname.startsWith("/portal") ? "/portal" : `/clients/${selected?.client_id}`;
  const [reports, setReports] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  // Scope MUST be chosen explicitly before any generate/download/view is
  // available. `null` = "user hasn't picked yet" → renders the picker card
  // and hides the rest.
  const [scope, setScope] = useState(null);
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!selected) return;
    api.get(`/reports/${selected.client_id}`).then((r) => setReports(r.data));
    api.get(`/clients/${selected.client_id}/business-units`)
      .then((r) => setBusinessUnits(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setBusinessUnits([]));
    // Reset scope whenever the active client changes — never carry a stale
    // business_unit_id across clients.
    setScope(null);
  }, [selected?.client_id]);

  useEffect(() => {
    if (!selected || !period) return;
    api.get(`/report-notes/${selected.client_id}/${period}`)
      .then((r) => setNotes(r.data.notes || ""))
      .catch(() => setNotes(""));
  }, [selected?.client_id, period]);

  // Map business_unit_id -> name for displaying the scope label on each row.
  const buNameMap = useMemo(() => {
    const m = new Map();
    businessUnits.forEach((b) => m.set(b.business_unit_id, b.name || "Unnamed property"));
    return m;
  }, [businessUnits]);

  // Filtered to the currently-selected scope so the listing matches what
  // the user is working on. When scope is null we hide the list entirely.
  const visibleReports = useMemo(() => {
    if (!scope) return [];
    if (scope === SCOPE_OVERARCHING) {
      return reports.filter((r) => !r.business_unit_id);
    }
    return reports.filter((r) => r.business_unit_id === scope);
  }, [reports, scope]);

  const scopeLabel = scope === SCOPE_OVERARCHING
    ? "Overarching (all properties)"
    : (buNameMap.get(scope) || "—");

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/report-notes/${selected.client_id}/${period}`, { notes });
      toast.success("Notes saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSavingNotes(false); }
  };

  const generate = async () => {
    if (!scope) {
      toast.info("Pick a property or 'Overarching' first.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/reports/generate", {
        client_id: selected.client_id,
        period_month: period,
        business_unit_id: scope === SCOPE_OVERARCHING ? null : scope,
      });
      const list = await api.get(`/reports/${selected.client_id}`);
      setReports(list.data);
      toast.success("Draft generated — open it to review and edit, then click 'Create PDF'");
      if (data?.report?.report_id) {
        navigate(`${portalPrefix}/reports/${data.report.report_id}/view`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const download = (r) => {
    fetch(`${API}/reports/${r.report_id}/pdf`, {
      credentials: "include",
    }).then((res) => {
      if (!res.ok) throw new Error("Download failed");
      return res.blob();
    }).then((blob) => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${selected.company_name}_${r.period_month}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    }).catch(() => toast.error("Download failed"));
  };

  const deleteReport = async (r) => {
    try {
      await api.delete(`/reports/${r.report_id}`);
      setReports((prev) => prev.filter((x) => x.report_id !== r.report_id));
      toast.success(`Report for ${r.period_month} deleted`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  if (!selected) return null;

  // Scope picker — always visible at the top. The rest of the page is
  // gated behind a real selection so the admin can't accidentally generate
  // an overarching report when they meant to scope to a property.
  const ScopePicker = (
    <Card className="rounded-sm border-zinc-200" data-testid="report-scope-picker">
      <CardContent className="p-6 space-y-4">
        <div>
          <div className="label-overline">Step 1 · Choose report scope</div>
          <p className="text-sm text-zinc-600 mt-1">
            Every report is now scoped explicitly. Pick <strong>Overarching</strong> for a
            consolidated cross-property view, or pick a single <strong>property</strong> to
            generate a focused report just for that site/business unit.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          <ScopeTile
            active={scope === SCOPE_OVERARCHING}
            onClick={() => setScope(SCOPE_OVERARCHING)}
            Icon={Globe2}
            title="Overarching"
            subtitle={`All ${businessUnits.length || "—"} ${businessUnits.length === 1 ? "property" : "properties"}`}
            testid="scope-overarching"
          />
          {businessUnits.map((b) => (
            <ScopeTile
              key={b.business_unit_id}
              active={scope === b.business_unit_id}
              onClick={() => setScope(b.business_unit_id)}
              Icon={Building2}
              title={b.name || "Unnamed property"}
              subtitle={b.domain || b.industry || "Property"}
              testid={`scope-bu-${b.business_unit_id}`}
            />
          ))}
        </div>
        {businessUnits.length === 0 && (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5">
            This client has no properties configured yet — only the overarching scope is available.
            Add properties on the Properties page if you want per-property reports.
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-8" data-testid="reports-page">
      <div>
        <div className="label-overline">Deliverables</div>
        <h1 className="font-display font-extrabold text-4xl tracking-tight">Monthly Reports</h1>
        <p className="text-sm text-zinc-500 mt-1">
          One-click consolidated PDF with AI executive summary, platform totals and worklog activities.
        </p>
      </div>

      {ScopePicker}

      {/* Everything below this line is gated on a real scope selection. */}
      {!scope ? (
        <Card className="rounded-sm border-dashed border-zinc-300 bg-zinc-50">
          <CardContent className="p-8 text-center text-sm text-zinc-500">
            <Layers className="h-6 w-6 mx-auto mb-2 text-zinc-400" />
            Pick a scope above to see generated reports and the draft generator.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex items-center gap-2 text-xs label-overline text-[#002FA7]"
            data-testid="reports-active-scope-banner">
            {scope === SCOPE_OVERARCHING
              ? <Globe2 className="h-3 w-3" />
              : <Building2 className="h-3 w-3" />}
            Scope: {scopeLabel}
            <button onClick={() => setScope(null)}
              className="ml-2 text-[10px] underline text-zinc-500 hover:text-zinc-700"
              data-testid="reports-change-scope">change</button>
          </div>

          {isAdmin && (
            <>
            <Card className="rounded-sm border-zinc-200">
              <CardContent className="p-6 space-y-4">
                <div className="flex flex-col md:flex-row gap-4 md:items-end">
                  <div className="space-y-2 flex-1">
                    <Label className="label-overline">Step 2 · Reporting month</Label>
                    <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
                      className="rounded-sm h-11" data-testid="report-month-input" />
                  </div>
                  <Button onClick={generate} disabled={busy}
                    className="rounded-sm h-11 bg-[#002FA7] hover:bg-[#00227A] text-white"
                    data-testid="generate-report-button">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                    Generate draft
                  </Button>
                </div>
                <p className="text-xs text-zinc-500">
                  Draft generation builds the AI analysis and charts for{" "}
                  <strong>{scopeLabel}</strong> but does NOT create the PDF —
                  you&apos;ll review and edit the report in the next screen, then click{" "}
                  <strong>Create PDF</strong>.
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-sm border-zinc-200">
              <CardContent className="p-6 space-y-3">
                <div>
                  <div className="label-overline">Activity notes for {period}</div>
                  <p className="text-sm text-zinc-500 mt-1">
                    Add anecdotal context that won&apos;t show up in the CSV data (e.g. <em>&ldquo;During May we built 5 new case study pages with SEO enhancements&rdquo;</em>). This text appears near the top of the PDF and is included in the AI&apos;s context.
                  </p>
                </div>
                <Textarea rows={5} value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="During May we…"
                  className="rounded-sm" data-testid="report-notes-input" />
                <Button size="sm" onClick={saveNotes} disabled={savingNotes}
                  className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white"
                  data-testid="report-notes-save-button">
                  <Save className="h-3.5 w-3.5 mr-1.5" /> {savingNotes ? "Saving…" : "Save notes"}
                </Button>
              </CardContent>
            </Card>
            </>
          )}

          <div>
            <div className="label-overline mb-3">
              Generated reports · {scopeLabel} · {visibleReports.length} total
            </div>
            <div className="border border-zinc-200 rounded-sm divide-y divide-zinc-200 bg-white">
              {visibleReports.map((r) => (
                <div key={r.report_id}
                  className="flex items-center gap-4 px-4 py-3 hover:bg-zinc-50 transition-colors"
                  data-testid={`report-row-${r.report_id}`}>
                  <div className="h-9 w-9 bg-zinc-900 text-white rounded-sm flex items-center justify-center flex-shrink-0">
                    <FileText className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display font-bold text-sm">{r.period_month}</div>
                      <Badge className={`rounded-sm ${r.status === "finalized" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}
                        data-testid={`report-status-${r.report_id}`}>
                        {r.status === "finalized" ? "PDF created" : "Draft"}
                      </Badge>
                      <Badge variant="outline" className="rounded-sm text-[10px] font-mono"
                        data-testid={`report-scope-${r.report_id}`}>
                        {r.business_unit_id
                          ? (buNameMap.get(r.business_unit_id) || "Property")
                          : "Overarching"}
                      </Badge>
                      {r.generated_at && (
                        <div className="inline-flex items-center gap-1 text-[11px] text-zinc-500 font-mono"
                          data-testid={`report-generated-${r.report_id}`}
                          title={`Generated ${new Date(r.generated_at).toISOString()}`}>
                          <Clock className="h-3 w-3" /> Generated {formatAuDateTime(r.generated_at)}
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 truncate mt-0.5">
                      {r.summary_md?.slice(0, 200) || "Monthly consolidated report"}…
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-sm flex-shrink-0"
                    onClick={() => navigate(`${portalPrefix}/reports/${r.report_id}/view`)}
                    data-testid={`view-report-${r.report_id}`}>
                    <Eye className="h-3.5 w-3.5 mr-1.5" /> View {r.status !== "finalized" && "& edit"}
                  </Button>
                  {r.status === "finalized" && r.pdf_path && (
                    <Button size="sm" className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white flex-shrink-0"
                      onClick={() => download(r)} data-testid={`download-report-${r.report_id}`}>
                      <Download className="h-3.5 w-3.5 mr-1.5" /> PDF
                    </Button>
                  )}
                  {isAdmin && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost"
                          className="rounded-sm text-red-600 hover:bg-red-50 flex-shrink-0"
                          data-testid={`delete-report-${r.report_id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-sm">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete the {r.period_month} report?</AlertDialogTitle>
                          <AlertDialogDescription>
                            The web report and (if created) PDF will be removed. You can
                            generate a fresh draft for {r.period_month} afterwards. This action
                            cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                          <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700"
                            onClick={() => deleteReport(r)}
                            data-testid={`confirm-delete-report-${r.report_id}`}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              ))}
              {visibleReports.length === 0 && (
                <div className="p-10 text-center text-sm text-zinc-500">
                  No {scope === SCOPE_OVERARCHING ? "overarching" : "property"} reports yet
                  {isAdmin ? " — generate one above." : "."}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ScopeTile({ active, onClick, Icon, title, subtitle, testid }) {
  return (
    <button type="button" onClick={onClick}
      data-testid={testid}
      className={`text-left rounded-sm border p-3 transition-colors flex items-start gap-2.5
        ${active
          ? "border-[#002FA7] bg-[#002FA7]/[0.05] ring-1 ring-[#002FA7]/40"
          : "border-zinc-200 hover:border-zinc-300 bg-white"}`}>
      <div className={`h-9 w-9 rounded-sm grid place-items-center flex-shrink-0
        ${active ? "bg-[#002FA7] text-white" : "bg-zinc-100 text-zinc-700"}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="font-bold text-sm truncate">{title}</div>
        <div className="text-[11px] text-zinc-500 truncate">{subtitle}</div>
      </div>
    </button>
  );
}
